import React, { useState, useRef, useEffect, useCallback } from 'react';
import Select from 'react-select';
import pb from '../../services/pocketbase';
import Modal from '../Modal/Modal';
import { FaPencilAlt, FaTrash, FaCloudUploadAlt, FaArrowsAlt, FaList, FaTh } from 'react-icons/fa';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import imageCompression from 'browser-image-compression';

// Types pour le DND
const ItemTypes = {
    IMAGE: 'image',
};

// Composant de miniature pour le glisser-déposer
const DraggableImage = ({ image, index, moveImage, handleDelete, isNew, editingProduct }) => {
    const ref = useRef(null);
    const [, drop] = useDrop({
        accept: ItemTypes.IMAGE,
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
            moveImage(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });
    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemTypes.IMAGE,
        item: { index, id: isNew ? image.name : image },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    drag(drop(ref));

    const imageUrl = isNew ? URL.createObjectURL(image) : pb.files.getURL(editingProduct, image);

    return (
        <div 
            ref={ref} 
            className="image-preview-item" 
            style={{ opacity: isDragging ? 0 : 1 }}
        >
            <FaArrowsAlt className="drag-handle-icon" />
            <img src={imageUrl} alt="Aperçu" className="image-preview-thumbnail" />
            <button 
                type="button" 
                onClick={() => handleDelete(isNew ? image.name : image)} 
                className="delete-image-btn"
            >
                &times;
            </button>
        </div>
    );
};

function ProductManager({ products, categories, colors, materials, pearlTypes, onProductAdded, onProductUpdated, onProductDeleted }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newProduct, setNewProduct] = useState({ name: '', description: '', price: '', category: [], images: [], is_sold: false, color: [], material: [], pearl_type: [] });
    const [editingProduct, setEditingProduct] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [viewMode, setViewMode] = useState(localStorage.getItem('productViewMode') || 'grid');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        // Save view mode choice to local storage
        localStorage.setItem('productViewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const showAddProductForm = () => {
        setEditingProduct(null);
        setNewProduct({ 
            name: '', 
            description: '', 
            price: '', 
            stock: 0, 
            is_unique: false,
            category: [], 
            images: [], 
            is_sold: false, 
            color: [], 
            material: [], 
            pearl_type: [] 
        });
        setIsModalOpen(true);
    };

    const showEditProductForm = (product) => {
        setEditingProduct(product);
        setNewProduct({
            name: product.name,
            description: product.description,
            price: product.price,
            stock: product.stock || 0,
            is_unique: product.is_unique || false,
            category: Array.isArray(product.category) ? product.category : (product.category ? [product.category] : []),
            is_sold: product.is_sold || false,
            color: product.color || [],
            material: product.material || [],
            pearl_type: product.pearl_type || [],
            images: [],
        });
        setIsModalOpen(true);
    };
    
    // Fonction manquante réintégrée
    const toggleSoldStatus = async (productId, currentStatus) => {
        try {
            const record = await pb.collection('products').update(productId, {
                is_sold: !currentStatus
            });
            const updatedProductWithData = await pb.collection('products').getOne(record.id, { expand: 'category' });
            onProductUpdated(updatedProductWithData);
        } catch (error) {
            console.error("Erreur lors de la mise à jour du statut du produit:", error);
            alert("Une erreur s'est produite lors du changement de statut.");
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setEditingProduct(null);
        setNewProduct({ name: '', description: '', price: '', category: [], images: [], is_sold: false, color: [], material: [], pearl_type: [] });
    };

    const handleSelectChange = (selectedOptions, action) => {
        const { name } = action;
        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setNewProduct({ ...newProduct, [name]: values });
    };

    const handleProductChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setNewProduct({ ...newProduct, [name]: checked });
        } else {
            setNewProduct({ ...newProduct, [name]: value });
        }
    };

    // Gestion personnalisée pour le champ de stock
    const handleStockChange = (e) => {
        let value = e.target.value;
        // Permet de vider le champ ou de ne contenir que des chiffres
        if (value === '' || /^\d*$/.test(value)) {
            setNewProduct({ ...newProduct, stock: value });
        }
    };

    // Empêche la modification des champs numériques avec la molette de la souris
    const preventWheel = (e) => {
        // Désactive la molette uniquement pour les champs numériques
        if (e.target.type === 'number') {
            e.target.blur();
            e.stopPropagation();
            setTimeout(() => e.target.focus(), 0);
        }
    };
    
    const handleFileChange = useCallback(async (files) => {
                const imageFiles = Array.from(files).slice(0, 10 - (editingProduct?.image?.length || 0) - newProduct.images.length);
        const compressedFiles = await Promise.all(imageFiles.map(async (file) => {
            const options = {
                maxSizeMB: 4.5, // Taille maximale cible en Mo
                maxWidthOrHeight: 1920, // Redimensionne l'image si elle est plus grande
                useWebWorker: true,
                initialQuality: 0.8 // Qualité initiale de compression
            };
            try {
                // Compresser l'image seulement si elle est plus grande que maxSizeMB
                if (file.size / 1024 / 1024 > options.maxSizeMB) {
                    console.log(`Compression de l'image: ${file.name}, taille originale: ${(file.size / 1024 / 1024).toFixed(2)} Mo`);
                    const compressedFile = await imageCompression(file, options);
                    console.log(`Taille compressée: ${(compressedFile.size / 1024 / 1024).toFixed(2)} Mo`);
                    return compressedFile;
                } else {
                    return file; // Retourner le fichier original s'il est déjà assez petit
                }
            } catch (error) {
                console.error('Erreur de compression:', error);
                return file; // En cas d'erreur, retourner le fichier original
            }
        }));

        setNewProduct(prev => ({
            ...prev,
            images: [...prev.images, ...compressedFiles]
        }));
    }, [newProduct.images]);

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange(e.dataTransfer.files);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDeleteNewImage = (filenameToDelete) => {
        setNewProduct(prev => ({
            ...prev,
            images: prev.images.filter(file => file.name !== filenameToDelete)
        }));
    };
    
    const handleDeleteExistingImage = (filenameToDelete) => {
        if (!editingProduct) return;

        setEditingProduct(prev => {
            if (!prev) return null;
            const updatedImages = (prev.image || []).filter(img => img !== filenameToDelete);
            return { ...prev, image: updatedImages };
        });
    };
    
    const moveImage = (dragIndex, hoverIndex) => {
        const existingImages = editingProduct?.image || [];
        const newImages = newProduct.images;
        const allImages = [...existingImages, ...newImages];
        const draggedImage = allImages[dragIndex];
        
        allImages.splice(dragIndex, 1);
        allImages.splice(hoverIndex, 0, draggedImage);
    
        const newExistingImages = [];
        const newNewImages = [];
    
        allImages.forEach(img => {
            if (typeof img === 'string') { // C'est une image existante (un nom de fichier)
                newExistingImages.push(img);
            } else { // C'est une nouvelle image (un objet File)
                newNewImages.push(img);
            }
        });
    
        setEditingProduct(prev => ({
            ...prev,
            image: newExistingImages,
        }));
        setNewProduct(prev => ({
            ...prev,
            images: newNewImages,
        }));
    };
    
    const generateSlug = (name) => {
        if (!name) return '';
        return name
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w\-]+/g, '')
            .replace(/\--+/g, '-');
    };

    const handleProductSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        setIsSubmitting(true);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        let baseSlug = generateSlug(newProduct.name);
        if (!baseSlug) {
            alert("Le nom du produit ne peut pas être vide.");
            return;
        }
        let finalSlug = baseSlug;
        let isUnique = false;
        let counter = 1;

        while (!isUnique) {
            try {
                const filter = editingProduct
                    ? `slug = "${finalSlug}" && id != "${editingProduct.id}"`
                    : `slug = "${finalSlug}"`;
                await pb.collection('products').getFirstListItem(filter, { signal });
                counter++;
                finalSlug = `${baseSlug}-${counter}`;
            } catch (error) {
                if (error.status === 404) {
                    isUnique = true;
                } else if (error.name === 'AbortError') {
                    console.log('Vérification du slug annulée.');
                    setIsSubmitting(false);
                    return;
                } else {
                    console.error("Erreur lors de la vérification de l'unicité du slug:", error);
                    alert("Une erreur est survenue lors de la vérification du slug.");
                    setIsSubmitting(false);
                    return;
                }
            }
        }

        try {
            let record;
            if (editingProduct) {
                // --- MISE À JOUR D'UN PRODUIT EXISTANT (EN 2 ÉTAPES) ---

                // Étape 1: Mettre à jour les métadonnées et la galerie d'images existantes.
                const metadataFormData = new FormData();
                metadataFormData.append('name', newProduct.name);
                metadataFormData.append('slug', finalSlug);
                metadataFormData.append('description', newProduct.description);
                metadataFormData.append('price', String(newProduct.price || 0));
                metadataFormData.append('stock', String(parseInt(newProduct.stock) || 0));
                metadataFormData.append('is_unique', String(!!newProduct.is_unique));
                metadataFormData.append('is_sold', String(!!newProduct.is_sold));
                
                if (newProduct.category) newProduct.category.forEach(id => metadataFormData.append('category', id));
                if (newProduct.color) newProduct.color.forEach(id => metadataFormData.append('color', id));
                if (newProduct.material) newProduct.material.forEach(id => metadataFormData.append('material', id));
                if (newProduct.pearl_type) newProduct.pearl_type.forEach(id => metadataFormData.append('pearl_type', id));

                const finalExistingImages = editingProduct.image || [];
                if (finalExistingImages.length > 0) {
                    finalExistingImages.forEach(imgName => metadataFormData.append('image', imgName));
                } else {
                    metadataFormData.append('image', ''); // Vide le champ image
                }

                record = await pb.collection('products').update(editingProduct.id, metadataFormData, { signal });

                // Étape 2: Ajouter les nouvelles images (si nécessaire).
                if (newProduct.images.length > 0) {
                    const newImagesFormData = new FormData();
                    newProduct.images.forEach(file => newImagesFormData.append('image', file));
                    record = await pb.collection('products').update(record.id, newImagesFormData, { signal });
                }

            } else {
                // --- CRÉATION D'UN NOUVEAU PRODUIT (EN 1 ÉTAPE) ---
                const createFormData = new FormData();
                createFormData.append('name', newProduct.name);
                createFormData.append('slug', finalSlug);
                createFormData.append('description', newProduct.description);
                createFormData.append('price', String(newProduct.price || 0));
                createFormData.append('stock', String(parseInt(newProduct.stock) || 0));
                createFormData.append('is_unique', String(!!newProduct.is_unique));
                createFormData.append('is_sold', String(!!newProduct.is_sold));

                if (newProduct.category) newProduct.category.forEach(id => createFormData.append('category', id));
                if (newProduct.color) newProduct.color.forEach(id => createFormData.append('color', id));
                if (newProduct.material) newProduct.material.forEach(id => createFormData.append('material', id));
                if (newProduct.pearl_type) newProduct.pearl_type.forEach(id => createFormData.append('pearl_type', id));
                if (newProduct.images) newProduct.images.forEach(file => createFormData.append('image', file));

                record = await pb.collection('products').create(createFormData, { signal });
            }

            const refreshedProduct = await pb.collection('products').getOne(record.id, {
                expand: 'category,color,material,pearl_type',
                requestKey: 'prod-fetch-' + crypto.randomUUID(),
                signal,
            });

            if (editingProduct) {
                onProductUpdated(refreshedProduct);
                alert('Produit mis à jour avec succès !');
            } else {
                onProductAdded(refreshedProduct);
                alert('Produit ajouté avec succès !');
            }
            handleCancel();
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Soumission du produit annulée.');
            } else {
                console.error("Erreur lors de la soumission du produit:", error);
                alert(`Erreur: ${error.message}`);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleProductDelete = async (productId) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
            try {
                await pb.collection('products').delete(productId);
                onProductDeleted(productId);
                alert('Produit supprimé avec succès.');
            } catch (error) {
                console.error("Erreur lors de la suppression du produit:", error);
                alert(`Erreur: ${error.message}`);
            }
        }
    };

    const filteredProducts = products.filter(product => {
        const statusMatch = (filterStatus === 'all') ||
            (filterStatus === 'for_sale' && !product.is_sold) ||
            (filterStatus === 'sold' && product.is_sold);
        const categoryMatch = (categoryFilter === 'all') ||
            (product.category && product.category.includes(categoryFilter));
        return statusMatch && categoryMatch;
    });

    const renderProductForm = () => {
        const categoryOptions = categories.map(c => ({ value: c.id, label: c.name }));
        const colorOptions = colors.map(c => ({ value: c.id, label: c.name }));
        const materialOptions = materials.map(m => ({ value: m.id, label: m.name }));
        const pearlTypeOptions = pearlTypes.map(p => ({ value: p.id, label: p.name }));
        const customStyles = {
            control: (provided) => ({ ...provided, backgroundColor: '#2d3748', borderColor: '#4a5568', color: 'white' }),
            menu: (provided) => ({ ...provided, backgroundColor: '#2d3748' }),
            option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? '#4a5568' : '#2d3748', color: 'white', ':hover': { backgroundColor: '#4a5568', } }),
            multiValue: (provided) => ({ ...provided, backgroundColor: '#4a5568' }),
            multiValueLabel: (provided) => ({ ...provided, color: 'white' }),
            input: (provided) => ({ ...provided, color: 'white' }),
            singleValue: (provided) => ({ ...provided, color: 'white' }),
        };

        const existingImages = editingProduct?.image || [];

        return (
            <form onSubmit={handleProductSubmit} className="dashboard-form">
                <div className="form-group">
                    <label htmlFor="name">Titre du produit</label>
                    <input 
                        type="text" 
                        id="name"
                        name="name" 
                        value={newProduct.name} 
                        onChange={handleProductChange} 
                        placeholder="Nom du produit" 
                        required 
                        className="dashboard-form"
                        style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '5px',
                            fontSize: '1rem',
                            backgroundColor: 'white',
                            color: '#2c3e50',
                            marginTop: '5px'
                        }}
                    />
                </div>
                
                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea 
                        id="description"
                        name="description" 
                        value={newProduct.description} 
                        onChange={handleProductChange} 
                        placeholder="Description du produit"
                        className="dashboard-form"
                        style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '5px',
                            fontSize: '1rem',
                            backgroundColor: 'white',
                            color: '#2c3e50',
                            marginTop: '5px',
                            minHeight: '120px',
                            resize: 'vertical'
                        }}
                    ></textarea>
                </div>
                <div className="form-group">
                    <label htmlFor="price">Prix</label>
                    <input 
                        type="number" 
                        id="price"
                        name="price" 
                        value={newProduct.price} 
                        onChange={handleProductChange} 
                        onWheel={preventWheel}
                        placeholder="Prix" 
                        step="0.01"
                        min="0"
                        className="dashboard-form"
                        style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '5px',
                            fontSize: '1rem',
                            backgroundColor: 'white',
                            color: '#2c3e50',
                            marginTop: '5px',
                            WebkitAppearance: 'textfield',
                            MozAppearance: 'textfield'
                        }}
                    />
                </div>
                
                <div className="form-group">
                    <label htmlFor="stock">Stock disponible</label>
                    <input 
                        type="number" 
                        id="stock"
                        name="stock" 
                        min={newProduct.is_unique ? 1 : 0}
                        disabled={newProduct.is_unique}
                        value={newProduct.stock === 0 ? '' : newProduct.stock} 
                        onChange={handleStockChange}
                        onWheel={preventWheel}
                        onFocus={(e) => e.target.select()}
                        placeholder="Quantité en stock"
                        className="dashboard-form"
                        style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '5px',
                            fontSize: '1rem',
                            backgroundColor: newProduct.is_unique ? '#f5f5f5' : 'white',
                            color: newProduct.is_unique ? '#999' : '#2c3e50',
                            marginTop: '5px',
                            WebkitAppearance: 'textfield',
                            MozAppearance: 'textfield',
                            cursor: newProduct.is_unique ? 'not-allowed' : 'auto'
                        }}
                    />
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                    <input
                        type="checkbox"
                        id="is_unique"
                        name="is_unique"
                        checked={newProduct.is_unique || false}
                        onChange={(e) => {
                            const isChecked = e.target.checked;
                            setNewProduct(prev => ({
                                ...prev,
                                is_unique: isChecked,
                                stock: isChecked ? 1 : prev.stock
                            }));
                        }}
                        style={{
                            marginRight: '10px',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                        }}
                    />
                    <label htmlFor="is_unique" style={{ margin: 0, cursor: 'pointer' }}>
                        Pièce unique
                    </label>
                </div>

                <label>Catégorie</label>
                <Select
                    name="category"
                    options={categoryOptions}
                    isMulti
                    styles={customStyles}
                    onChange={handleSelectChange}
                    value={categoryOptions.filter(option => newProduct.category.includes(option.value))}
                    placeholder="Sélectionner une ou plusieurs catégories"
                    required
                />

                <label>Couleurs</label>
                <Select
                    name="color"
                    options={colorOptions}
                    isMulti
                    styles={customStyles}
                    onChange={handleSelectChange}
                    value={colorOptions.filter(option => newProduct.color.includes(option.value))}
                    placeholder="Sélectionner une ou plusieurs couleurs"
                />

                <label>Matières</label>
                <Select
                    name="material"
                    options={materialOptions}
                    isMulti
                    styles={customStyles}
                    onChange={handleSelectChange}
                    value={materialOptions.filter(option => newProduct.material.includes(option.value))}
                    placeholder="Sélectionner une ou plusieurs matières"
                />

                <label>Types de perle</label>
                <Select
                    name="pearl_type"
                    options={pearlTypeOptions}
                    isMulti
                    styles={customStyles}
                    onChange={handleSelectChange}
                    value={pearlTypeOptions.filter(option => newProduct.pearl_type.includes(option.value))}
                    placeholder="Sélectionner un ou plusieurs types de perles"
                />

                <div className="form-group">
                    <label>Images</label>
                    <div 
                        className={`drag-and-drop-area ${isDragging ? 'dragging' : ''}`}
                        onDragOver={handleDrag}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current.click()}
                    >
                        <FaCloudUploadAlt size={48} className="upload-icon" />
                        <p>{isDragging ? 'Déposez les images ici !' : 'Glissez-déposez des images ou cliquez pour sélectionner'}</p>
                        <p className="sub-text">{`${existingImages.length + newProduct.images.length} image(s) sélectionnée(s)`}</p>
                        <p className="sub-text">Fichiers acceptés : PNG, JPG, JPEG, GIF, SVG</p>
                        <input
                            type="file"
                            name="images"
                            multiple
                            ref={fileInputRef}
                            onChange={(e) => handleFileChange(e.target.files)}
                            style={{ display: 'none' }}
                            accept=".png,.jpg,.jpeg,.gif,.svg"
                        />
                    </div>
                </div>

                {/* Prévisualisation des images */}
                <div className="dnd-provider-container">
                    {(newProduct.images.length > 0 || existingImages.length > 0) && (
                        <div className="images-preview-container">
                            <h5>Aperçu des images (glissez-déposez pour réordonner)</h5>
                            <div className="images-grid">
                                {existingImages.map((img, index) => (
                                    <DraggableImage
                                        key={img}
                                        image={img}
                                        index={index}
                                        moveImage={moveImage}
                                        handleDelete={handleDeleteExistingImage}
                                        isNew={false}
                                        editingProduct={editingProduct}
                                    />
                                ))}
                                {newProduct.images.map((file, index) => (
                                    <DraggableImage
                                        key={file.name + index}
                                        image={file}
                                        index={existingImages.length + index}
                                        moveImage={moveImage}
                                        handleDelete={handleDeleteNewImage}
                                        isNew={true}
                                        editingProduct={editingProduct}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="form-control-checkbox">
                    <label htmlFor="is_sold">Marquer comme vendu</label>
                    <input type="checkbox" id="is_sold" name="is_sold" checked={newProduct.is_sold} onChange={handleProductChange} />
                </div>

                <div className="form-actions">
                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Enregistrement...' : (editingProduct ? 'Mettre à jour' : 'Ajouter')}
                    </button>
                    <button type="button" onClick={handleCancel} className="btn-cancel" disabled={isSubmitting}>Annuler</button>
                </div>
            </form>
        );
    };

    return (
            <div className="product-manager">
            <div className="list-header">
                <h4>Gestion des Produits</h4>
                <div className="filter-container">
                    <div>
                        <label htmlFor="status-filter">Statut :</label>
                        <select
                            id="status-filter"
                            className="status-filter-select"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">Tous</option>
                            <option value="for_sale">En Vente</option>
                            <option value="sold">Vendus</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="category-filter">Catégorie :</label>
                        <select
                            id="category-filter"
                            className="status-filter-select"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">Toutes</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="view-mode-controls">
                    <button onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'active' : ''} aria-label="Affichage en liste">
                        <FaList />
                    </button>
                    <button onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'active' : ''} aria-label="Affichage en grille">
                        <FaTh />
                    </button>
                </div>
                <button onClick={showAddProductForm}>Ajouter un produit</button>
            </div>

            {viewMode === 'list' ? (
                <div className="dashboard-table-container product-table-desktop">
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Nom</th>
                                <th>Catégorie</th>
                                <th>Prix</th>
                                <th>Stock</th>
                                <th>Statut</th>
                                <th>Changer Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.length > 0 ? (
                                filteredProducts.map(product => (
                                    <tr key={product.id}>
                                        <td>
                                            {product.image && product.image.length > 0 ? (
                                                <img src={pb.files.getURL(product, product.image[0])} alt={product.name} className="table-thumbnail" />
                                            ) : (
                                                <div className="table-thumbnail-placeholder">?</div>
                                            )}
                                        </td>
                                        <td>{product.name}</td>
                                        <td>{product.expand?.category?.map(c => c.name).join(', ') || 'N/A'}</td>
                                        <td>{product.price} €</td>
                                        <td>{product.is_unique ? 'Pièce unique' : (product.stock > 0 ? product.stock : 'Épuisé')}</td>
                                        <td>
                                            <span className={`status-badge ${product.is_sold ? 'status-sold' : 'status-for-sale'}`}>
                                                {product.is_sold ? 'Vendu' : 'En Vente'}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className={`action-btn ${product.is_sold ? 'btn-set-for-sale' : 'btn-set-sold'}`}
                                                onClick={() => toggleSoldStatus(product.id, product.is_sold)}
                                            >
                                                {product.is_sold ? 'Remettre en vente' : 'Marquer vendu'}
                                            </button>
                                        </td>
                                        <td className="actions-cell">
                                            <button onClick={() => showEditProductForm(product)} className="btn-edit action-btn"><FaPencilAlt /> <span>Modifier</span></button>
                                            <button onClick={() => handleProductDelete(product.id)} className="btn-delete action-btn"><FaTrash /> <span>Supprimer</span></button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">Aucun produit trouvé.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="product-grid-view">
                    {filteredProducts.length > 0 ? (
                        filteredProducts.map(product => (
                            <div key={product.id} className="product-card">
                                <div className="product-card-image">
                                    {product.image && product.image.length > 0 ? (
                                        <img src={pb.files.getURL(product, product.image[0])} alt={product.name} />
                                    ) : (
                                        <div className="table-thumbnail-placeholder">?</div>
                                    )}
                                    <span className={`status-badge ${product.is_sold ? 'status-sold' : 'status-for-sale'}`}>
                                        {product.is_sold ? 'Vendu' : 'En Vente'}
                                    </span>
                                </div>
                                <div className="product-card-info">
                                    <h5>{product.name}</h5>
                                    <p>{product.price} €</p>
                                    <p>Stock: {product.is_unique ? 'Unique' : (product.stock > 0 ? product.stock : 'Épuisé')}</p>
                                </div>
                                <div className="product-card-status-action">
                                     <button 
                                        className={`toggle-status-btn ${product.is_sold ? 'btn-set-for-sale' : 'btn-set-sold'}`}
                                        onClick={() => toggleSoldStatus(product.id, product.is_sold)}
                                    >
                                        {product.is_sold ? 'Remettre en vente' : 'Marquer comme vendu'}
                                    </button>
                                </div>
                                <div className="product-card-actions">
                                    <button onClick={() => showEditProductForm(product)} className="btn-edit"><FaPencilAlt /> Modifier</button>
                                    <button onClick={() => handleProductDelete(product.id)} className="btn-delete"><FaTrash /> Supprimer</button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p>Aucun produit trouvé.</p>
                    )}
                </div>
            )}

            {/* --- Mobile Card View --- */}
            <div className="product-list-mobile">
                {filteredProducts.length > 0 ? (
                    filteredProducts.map(product => (
                        <div key={product.id} className="product-card-mobile">
                            <div className="card-mobile-header">
                                <img
                                    src={product.image && product.image.length > 0 ? pb.files.getURL(product, product.image[0]) : 'placeholder.png'}
                                    alt={product.name}
                                    className="card-mobile-image"
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100'; }}
                                />
                                <div className="card-mobile-title">
                                    <h4>{product.name}</h4>
                                    <p>{product.price ? `${product.price.toFixed(2)}€` : 'N/A'}</p>
                                </div>
                            </div>
                            <div className="card-mobile-details">
                                <p><strong>Catégorie:</strong> {Array.isArray(product.expand?.category) ? product.expand.category.map(c => c.name).join(', ') : product.expand?.category?.name || 'N/A'}</p>
                                <div className={`status-badge ${product.is_sold ? 'status-sold' : 'status-for-sale'}`}>
                                    {product.is_sold ? 'Vendu' : 'En Vente'}
                                </div>
                            </div>
                            <div className="card-mobile-actions">
                                <button
                                    className={`toggle-status-btn ${product.is_sold ? 'btn-set-for-sale' : 'btn-set-sold'}`}
                                    onClick={() => toggleSoldStatus(product.id, product.is_sold)}
                                >
                                    {product.is_sold ? 'Remettre en vente' : 'Marquer comme vendu'}
                                </button>
                                <div className="item-actions">
                                    <button onClick={() => showEditProductForm(product)} className='edit-btn' title="Modifier"><FaPencilAlt /> Modifier</button>
                                    <button onClick={() => handleProductDelete(product.id)} className='delete-btn' title="Supprimer"><FaTrash /> Supprimer</button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-list-message">
                        <p>Aucun produit trouvé pour ce filtre.</p>
                    </div>
                )}
            </div>

    {/* --- Mobile Card View --- */}
    <div className="product-list-mobile">
        {filteredProducts.length > 0 ? (
            filteredProducts.map(product => (
                <div key={product.id} className="product-card-mobile">
                    <div className="card-mobile-header">
                        <img
                            src={product.image && product.image.length > 0 ? pb.files.getURL(product, product.image[0]) : 'placeholder.png'}
                            alt={product.name}
                            className="card-mobile-image"
                            onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100'; }}
                        />
                        <div className="card-mobile-title">
                            <h4>{product.name}</h4>
                            <p>{product.price ? `${product.price.toFixed(2)}€` : 'N/A'}</p>
                        </div>
                    </div>
                    <div className="card-mobile-details">
                        <p><strong>Catégorie:</strong> {Array.isArray(product.expand?.category) ? product.expand.category.map(c => c.name).join(', ') : product.expand?.category?.name || 'N/A'}</p>
                        <div className={`status-badge ${product.is_sold ? 'status-sold' : 'status-for-sale'}`}>
                            {product.is_sold ? 'Vendu' : 'En Vente'}
                        </div>
                    </div>
                    <div className="card-mobile-actions">
                        <button
                            className={`toggle-status-btn ${product.is_sold ? 'btn-set-for-sale' : 'btn-set-sold'}`}
                            onClick={() => toggleSoldStatus(product.id, product.is_sold)}
                        >
                            {product.is_sold ? 'Remettre en vente' : 'Marquer comme vendu'}
                        </button>
                        <div className="item-actions">
                            <button onClick={() => showEditProductForm(product)} className='edit-btn' title="Modifier"><FaPencilAlt /> Modifier</button>
                            <button onClick={() => handleProductDelete(product.id)} className='delete-btn' title="Supprimer"><FaTrash /> Supprimer</button>
                        </div>
                    </div>
                </div>
            ))
        ) : (
            <div className="empty-list-message">
                <p>Aucun produit trouvé pour ce filtre.</p>
            </div>
        )}
    </div>

            <Modal
                isOpen={isModalOpen}
                onClose={handleCancel}
                title={editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
            >
                {renderProductForm()}
            </Modal>
        </div>
    );
}

export default ProductManager;